/**
 * FGA Manager Component
 *
 * Root component for the FGA Manager web component.
 * Provides inputs for configuration and outputs for events.
 *
 * @packageDocumentation
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  HostBinding,
  effect,
  signal,
  booleanAttribute,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { TokenProviderService } from './services/token-provider.service';
import { GroupStateService } from './services/group-state.service';
import { UserStateService } from './services/user-state.service';
import { FgaApiService } from './services/fga-api.service';
import { JwtDecoderService } from './services/jwt-decoder.service';
import { UserEnrichmentService } from './services/user-enrichment.service';
import { Auth0OrganisationService } from './services/auth0-org.service';
import { M2mClientService } from './services/m2m-client.service';
import { LoggerService } from './services/logger.service';
import { ErrorStateService } from './services/error-state.service';
import { FgaErrorEvent, GroupSelectedEvent, UserSelectedEvent, DataChangedEvent, LoadingStateChangedEvent, FGA_ERROR_CODES, UnauthorizedErrorSource } from './models/api.model';
import { LogEvent } from './models/log-event.model';
import { FgaGroup} from './models/group.model';
import { createFgaUserId, createFgaOrganizationId } from './models/fga-constants';
import { FgaGroupsListComponent } from './components/fga-groups-list/fga-groups-list.component';
import { FgaGroupViewComponent } from './components/fga-group-view/fga-group-view.component';
import { FgaUnauthorizedComponent } from './components/fga-unauthorized/fga-unauthorized.component';
import { FgaNavBarComponent, FgaView } from './components/fga-nav-bar/fga-nav-bar.component';
import { FgaOrgUsersViewComponent } from './components/fga-org-users-view/fga-org-users-view.component';
import { FGA_MANAGER_VERSION } from './version';
import { NgKitProgressSpinnerComponent } from '@wtw/angularkit/ngkit-progress-spinner';
import { NgKitInlineMessageComponent } from '@wtw/angularkit/ngkit-inline-message';

/**
 * Root component for the FGA Manager web component.
 *
 * Provides a management interface for Auth0 FGA groups and users.
 * Configure via attributes and listen for events in host applications.
 *
 * @usageNotes
 *
 * ### Basic usage in HTML (organisation ID from token):
 * ```html
 * <fga-manager
 *   fga-api-base="http://localhost:3000"
 * ></fga-manager>
 * ```
 *
 * ### With explicit organisation ID:
 * ```html
 * <fga-manager
 *   organisation-id="org_demo"
 *   fga-api-base="http://localhost:3000"
 * ></fga-manager>
 * ```
 *
 * ### With event listeners:
 * ```html
 * <fga-manager
 *   id="manager"
 *   fga-api-base="/api"
 * ></fga-manager>
 * <script>
 *   const manager = document.getElementById('manager');
 *   manager.addEventListener('initialized', () => console.log('Ready!'));
 *   manager.addEventListener('error', (e) => console.error(e.detail));
 * </script>
 * ```
 *
 * ### In Angular:
 * ```html
 * <fga-manager
 *   [fga-api-base]="fgaApiBase"
 *   (initialized)="onInit()"
 *   (error)="onError($event)"
 * ></fga-manager>
 * ```
 */
// Input aliases are required for Web Components - maps kebab-case HTML attributes to camelCase properties
/* eslint-disable @angular-eslint/no-input-rename */
@Component({
  selector: 'fga-manager',
  imports: [CommonModule, FgaGroupsListComponent, FgaGroupViewComponent, FgaUnauthorizedComponent, FgaNavBarComponent, FgaOrgUsersViewComponent, NgKitProgressSpinnerComponent, NgKitInlineMessageComponent],
  templateUrl: './fga-manager.component.html',
  styleUrls: ['./fga-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA], // Added to support web components
  providers: [GroupStateService, UserStateService, ErrorStateService, Auth0OrganisationService, M2mClientService] // Element-scoped state per instance
})
export class FgaManagerComponent implements OnInit, OnChanges {
  // ---------------------------------------------------------------------------
  // Required Inputs
  // ---------------------------------------------------------------------------

  /**
   * Organisation ID for scoping FGA operations.
   * Maps from `organisation-id` attribute.
   *
   * If not provided, will be extracted from the Auth0 token.
   * Token organisation ID takes precedence if both are provided.
   *
   * @example 'org_demo'
   */
  @Input({ alias: 'organisation-id' })
  organisationId?: string;  

  /**
   * Human-readable organisation name for display purposes.
   * Maps from `organisation-name` attribute.
   *
   * If not provided, will be extracted from the Auth0 token `org_name`
   * claim when present. Falls back to the organisation ID where no name
   * is available.
   *
   * @example 'Acme Corp'
   */
  @Input({ alias: 'organisation-name' })
  organisationName?: string;

  /**
   * Base URL for the FGA proxy API.
   * Maps from `fga-api-base` attribute.
   *
   * @example 'http://localhost:3000' or '/api'
   */
  @Input({ required: true, alias: 'fga-api-base' })
  fgaApiBase!: string;

  /**
   * Access token for user identification.
   * When provided, bypasses the Custom Event token exchange.
   * Maps from `access-token` attribute.
   *
   * @example 'eyJhbGciOiJSUzI1NiIs...'
   */
  @Input({ alias: 'access-token' })
  accessToken?: string;

  /**
   * FGA Store ID.
   * Required when using direct FGA API access.
   * Maps from `store-id` attribute.
   */
  @Input({ alias: 'store-id' })
  storeId?: string;

  /**
   * Azure APIM Subscription Key.
   * Required when using Azure Front Door/APIM.
   * Maps from `fga-subscription-key` attribute.
   */
  @Input({ alias: 'fga-subscription-key' })
  fgaSubscriptionKey?: string;

  /**
   * Auth0 Management API proxy URL.
   * Maps from `auth0-management-api-base` attribute.
   * Use this when your proxy already exposes the `/api/v2` path.
   *
   * @example 'http://localhost:3001/api/v2'
   */
  @Input({ alias: 'auth0-management-api-base' })
  auth0ManagementApiBase?: string;

  /**
   * Subscription key for Auth0 Management API proxy.
   * Required when the auth0-management-proxy requires subscription key authentication.
   * Maps from `auth0-mapi-subscription-key` attribute.
   *
   * @example 'abc123def456'
   */
  @Input({ alias: 'auth0-mapi-subscription-key' })
  auth0MapiSubscriptionKey?: string;

  // ---------------------------------------------------------------------------
  // Optional Inputs
  // ---------------------------------------------------------------------------

  /**
   * When true, disables all write operations (create, update, delete).
   * UI will hide or disable modification controls.
   *
   * @default false
   */
  @Input()
  readonly = false;

  /**
   * When true, the component handles unauthorized (401/403) errors internally
   * by displaying its own unauthorized screen.
   * When false, the component only emits the `errorEvent` and lets
   * the host application handle the error (e.g., navigate to a custom page).
   *
   * @default false
   */
  @Input({ alias: 'handle-unauthorized-internally', transform: booleanAttribute })
  handleUnauthorizedInternally = false;

  // ---------------------------------------------------------------------------
  // Outputs
  // ---------------------------------------------------------------------------

  /**
   * Emitted when the component has successfully initialized.
   * At this point, the token has been obtained and the component is ready.
   */
  @Output()
  initialized = new EventEmitter<void>();

  /**
   * Emitted when an error occurs during initialization or operation.
   */
  @Output()
  errorEvent = new EventEmitter<FgaErrorEvent>();

  /**
   * Emitted when a group is selected in the UI.
   */
  @Output()
  groupSelectedCustom = new EventEmitter<GroupSelectedEvent>();

  /**
   * Emitted when a user is selected in the UI.
   */
  @Output()
  userSelectedCustom = new EventEmitter<UserSelectedEvent>();

  /**
   * Emitted when data is modified (group/user/membership created, updated, or deleted).
   */
  @Output()
  dataChangedCustom = new EventEmitter<DataChangedEvent>();

  /**
   * Emitted when the loading state changes.
   * Allows host applications to show loading indicators or disable interactions.
   */
  @Output()
  loadingStateChanged = new EventEmitter<LoadingStateChangedEvent>();

  /**
   * Emitted when a log event occurs within the component.
   * Allows host applications to integrate with their logging infrastructure.
   * Events include debug, info, warn, and error levels.
   */
  @Output()
  logEvent = new EventEmitter<LogEvent>();

  /**
   * Emitted when the user clicks "Request Access" on the unauthorized screen.
   * Host applications should navigate to access request form.
   */
  @Output()
  requestAccessEvent = new EventEmitter<void>();

  /**
   * Emitted when the user clicks "Retry" on the unauthorized screen.
   * Host applications may want to refresh tokens before retry.
   */
  @Output()
  retryEvent = new EventEmitter<void>();

  // ---------------------------------------------------------------------------
  // Services
  // ---------------------------------------------------------------------------

  private readonly tokenProvider = inject(TokenProviderService);
  private readonly fgaApi = inject(FgaApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly el = inject(ElementRef);
  private readonly jwtDecoder = inject(JwtDecoderService);
  protected readonly groupStateService = inject(GroupStateService);
  private readonly userEnrichmentService = inject(UserEnrichmentService);
  private readonly auth0OrgService = inject(Auth0OrganisationService);
  private readonly m2mClientService = inject(M2mClientService);
  private readonly loggerService = inject(LoggerService);
  protected readonly errorStateService = inject(ErrorStateService);

  /** Exposed for template binding - delegates to GroupStateService signals */
  protected readonly groupState = {
    groups: () => this.groupStateService.groups(),
    sortedGroups: () => this.groupStateService.sortedGroups(),
    loading: () => this.groupStateService.loading(),
    error: () => this.groupStateService.error(),
    hasGroups: () => this.groupStateService.hasGroups(),
    selectedGroupId: () => this.groupStateService.selectedGroupId(),
    selectedGroup: () => this.groupStateService.selectedGroup(),
    addingGroup: () => this.groupStateService.addingGroup(),
    addGroupError: () => this.groupStateService.addGroupError(),
    groupNames: () => this.groupStateService.groupNames(),
  };

  protected readonly userState = inject(UserStateService);

  // ---------------------------------------------------------------------------
  // Component State
  // ---------------------------------------------------------------------------

  /** Active navigation view */
  protected activeView: FgaView = 'groups';

  protected onViewChange(view: FgaView): void {
    this.activeView = view;
    this.cdr.markForCheck();
  }

  /** Indicates if the component has been initialized */
  protected isInitialized = false;

  /**
   * Whether the current user is an organisation-level AuthZ_Admin.
   */
  protected readonly isAuthzAdmin = signal(false);

  /** Stores initialization error if one occurred */
  protected initError: string | null = null;

  /** Track previous loading state to detect changes */
  private previousLoadingState = false;

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  constructor() {
    // Wire up logger service to emit log events to host
    this.loggerService.logs$.subscribe(event => {
      this.logEvent.emit(event);
    });

    // Watch loading state changes and emit events
    effect(() => {
      const currentLoading = this.groupStateService.loading();
      
      // Only emit when state actually changes
      if (currentLoading !== this.previousLoadingState) {
        this.previousLoadingState = currentLoading;
        this.loadingStateChanged.emit({
          loading: currentLoading,
          source: 'groups'
        });
      }
    });

    // Watch for unauthorized state changes and emit error event
    effect(() => {
      const errorDetails = this.errorStateService.errorDetails();
      if (errorDetails) {
        const errorCode = errorDetails.status === 401 
          ? FGA_ERROR_CODES.UNAUTHORIZED 
          : FGA_ERROR_CODES.FORBIDDEN;
        this.errorEvent.emit({
          code: errorCode,
          message: errorDetails.message
        });
        this.cdr.markForCheck();
      }
    });

    // Watch for user enrichment auth errors and propagate to error state
    // This handles 401/403 from Auth0 Management API (user enrichment proxy)
    effect(() => {
      const enrichmentError = this.userEnrichmentService.lastAuthError();
      if (enrichmentError) {
        // Only propagate if we don't already have an error to avoid overwriting
        // FGA errors with enrichment errors (FGA errors are more critical)
        if (!this.errorStateService.isUnauthorized()) {
          this.errorStateService.setUnauthorized({
            status: enrichmentError.status,
            source: 'auth0',
            message: enrichmentError.message,
            timestamp: enrichmentError.timestamp
          });
          this.cdr.markForCheck();
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Host Bindings
  // ---------------------------------------------------------------------------

  /**
   * Applies the readonly class to the host element when readonly is true.
   */
  @HostBinding('class.readonly')
  get isReadonly(): boolean {
    return this.readonly;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle Hooks
  // ---------------------------------------------------------------------------

  /**
   * Initializes the component.
   * Validates configuration and obtains authentication token.
   */
  ngOnInit(): void {
    this.initialize();
  }

  /**
   * Handles input changes after initialization.
   * Re-initializes if critical configuration changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // Re-initialize if organisationId or fgaApiBase changes after init
    if (this.isInitialized) {
      if (changes['organisationId'] || changes['fgaApiBase']) {
        this.initialize();
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  /**
   * Performs component initialization.
   * Validates inputs, configures API service, obtains token, and extracts organisation ID.
   * Token can be provided via access-token attribute or obtained via Custom Event exchange.
   */
  private async initialize(): Promise<void> {
    // Reset state
    this.isInitialized = false;
    this.initError = null;
    this.isAuthzAdmin.set(false);
    this.groupStateService.reset();
    this.userState.reset();

    // Validate required fga-api-base input
    if (!this.fgaApiBase) {
      const errorEvent: FgaErrorEvent = {
        code: FGA_ERROR_CODES.MISSING_CONFIG,
        message: 'Required input "fga-api-base" must be provided'
      };
      this.initError = errorEvent.message;
      this.errorEvent.emit(errorEvent);
      return;
    }

    // Configure API service
    this.fgaApi.setApiBase(this.fgaApiBase);
    if (this.storeId) {
      this.fgaApi.setStoreId(this.storeId);
    }
    if (this.fgaSubscriptionKey) {
      this.fgaApi.setSubscriptionKey(this.fgaSubscriptionKey);
    }

    // Configure user enrichment service
    let enrichmentApiBase = this.fgaApiBase;

    if (this.auth0ManagementApiBase) {
      enrichmentApiBase = this.normalizeUrl(this.auth0ManagementApiBase);
    }

    this.userEnrichmentService.configure({
      apiBase: enrichmentApiBase,
      accessToken: this.accessToken || undefined,
      subscriptionKey: this.auth0MapiSubscriptionKey || undefined,
      timeout: 5000,
      retries: 2,
      appendApiPath: false
    });

    // Configure Auth0 Organisation Service (element-scoped)
    // Uses the proxy URL as-is � the auth0-management-proxy prepends /api/v2
    if (enrichmentApiBase) {
      this.auth0OrgService.configure({
        apiBase: enrichmentApiBase,
        accessToken: this.accessToken || undefined,
        subscriptionKey: this.auth0MapiSubscriptionKey || undefined,
        organisationId: this.organisationId || undefined,
        timeout: 10000,
        cacheTtl: 300000,
      });

      // Connect org service as enrichment provider — enrichUsers() will
      // try the org endpoint first before falling back to Lucene batch
      this.userEnrichmentService.setOrgProvider(this.auth0OrgService);
      // Configure M2M client service (element-scoped)
      this.m2mClientService.configure({
        apiBase: enrichmentApiBase,
        accessToken: this.accessToken || '',
        subscriptionKey: this.auth0MapiSubscriptionKey || undefined,
        organisationId: this.organisationId || '',
      });    }

    this.loggerService.debug('FgaManager', 'Config', {
      fgaApiBase: this.fgaApiBase,
      auth0ManagementApiBase: this.auth0ManagementApiBase || 'NOT SET',
      storeId: this.storeId,
      fgaSubscriptionKey: this.fgaSubscriptionKey ? 'SET' : 'NOT SET',
      auth0MapiSubscriptionKey: this.auth0MapiSubscriptionKey ? 'SET' : 'NOT SET',
      accessToken: this.accessToken ? 'SET' : 'NOT SET',
      enrichmentEnabled: !!enrichmentApiBase && !!this.accessToken,
      auth0OrgServiceConfigured: this.auth0OrgService.configured()
    });

    // Attempt to obtain token
    let token: string;
    this.loggerService.debug('FgaManager', `accessToken input: ${this.accessToken ? 'present' : 'missing'}`);
    if (this.accessToken) {
      token = this.accessToken;
      // Cache it in the token provider for the interceptor to use
      this.tokenProvider.setToken(token);
      this.loggerService.debug('FgaManager', 'Token set in provider from attribute');
    } else {
      this.loggerService.debug('FgaManager', 'Attempting Custom Event token exchange...');
      try {
        token = await this.tokenProvider.getToken();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        const isTimeout = errorMessage.includes('timeout');

        const errorEvent: FgaErrorEvent = {
          code: isTimeout ? FGA_ERROR_CODES.TOKEN_TIMEOUT : FGA_ERROR_CODES.INIT_FAILED,
          message: `Initialization failed: ${errorMessage}`
        };
        this.initError = errorEvent.message;
      this.errorEvent.emit(errorEvent);
        return;
      }
    }

    // Extract organisation ID from token if not provided
    const tokenOrgId = this.jwtDecoder.extractOrganisationId(token);
    const resolvedOrgId = this.organisationId || tokenOrgId;

    if (!resolvedOrgId) {
      const errorEvent: FgaErrorEvent = {
        code: FGA_ERROR_CODES.MISSING_ORG_ID,
        message: 'Organisation ID not provided and not found in token. Set organisation-id attribute or include org_id claim in token.'
      };
      this.initError = errorEvent.message;
      this.errorEvent.emit(errorEvent);
      return;
    }

    // Update organisationId if extracted from token
    if (!this.organisationId && tokenOrgId) {
      this.organisationId = tokenOrgId;
    }

    // Resolve organisation name from token when not supplied by the host
    if (!this.organisationName) {
      const tokenOrgName = this.jwtDecoder.extractOrganisationName(token);
      if (tokenOrgName) {
        this.organisationName = tokenOrgName;
      }
    }

    // Update Auth0 Organisation Service with resolved org ID (may differ from initial config)
    if (this.auth0OrgService.configured() && this.organisationId) {
      this.auth0OrgService.setOrganisationId(this.organisationId);
    }

    // Update M2M client service with resolved org ID
    if (this.m2mClientService.configured() && this.organisationId) {
      this.m2mClientService.setOrganisationId(this.organisationId);
    }

    this.isInitialized = true;
    this.loggerService.info('FgaManager', `Version: ${FGA_MANAGER_VERSION}`);

    // Ensure data-version is on the DOM before emitting, so listeners
    // can read it immediately (HostBinding waits for change detection).
    this.el.nativeElement.setAttribute('data-version', FGA_MANAGER_VERSION);

    this.initialized.emit();
    this.cdr.markForCheck();

    // Load groups after successful initialization
    this.loadGroups();

    // Determine whether the current user is an AuthZ_Admin
    this.checkAuthzAdmin(token);

    // Prime Auth0 user cache in background so member/owner lists show emails
    this.primeAuth0Users();

    // Prime M2M client cache in background so M2M members show friendly names
    this.primeM2MClients();
  }

  /**
   * Checks whether the current user holds the organisation-level
   * `authz_admin` relation and updates {@link isAuthzAdmin} accordingly.
   *
   * The user identity is taken from the token `sub` claim and the
   * organisation from the resolved organisation ID. On any failure (missing
   * claim, network/API error) the flag is left as false so that admin-only
   * controls remain hidden (fail-safe).
   *
   * @param token - The JWT used to identify the current user
   */
  private checkAuthzAdmin(token: string): void {
    const sub = this.jwtDecoder.decode(token).sub;

    if (!sub || !this.organisationId) {
      this.isAuthzAdmin.set(false);
      this.cdr.markForCheck();
      return;
    }

    const user = createFgaUserId(sub);
    const object = createFgaOrganizationId(this.organisationId);

    this.fgaApi.checkPermission(user, 'authz_admin', object).subscribe({
      next: allowed => {
        this.isAuthzAdmin.set(allowed);
        this.cdr.markForCheck();
      },
      error: err => {
        this.loggerService.warn('FgaManager', 'AuthZ_Admin check failed; hiding admin controls', {
          error: err instanceof Error ? err.message : String(err),
        });
        this.isAuthzAdmin.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Eagerly fetch all organisation members from Auth0 Management API
   * to pre-populate the user enrichment cache.
   *
   * When the cache is warm, group user lists immediately display
   * email addresses instead of raw user IDs.
   *
   * Runs in background � failures are non-critical since enrichment
   * also happens lazily when individual group users are loaded.
   */
  private async primeAuth0Users(): Promise<void> {
    if (!this.auth0OrgService.configured() || !this.organisationId) {
      return;
    }

    try {
      this.loggerService.debug('FgaManager', 'Priming Auth0 user cache via org members endpoint');
      const orgMembers = await this.auth0OrgService.loadMembersByOrgId(this.organisationId!);

      if (orgMembers.length > 0) {
        // Convert Auth0 user_id to FGA format for cache population
        const fgaUserIds = orgMembers.map(m => `user:${m.user_id}`);
        const populated = this.userEnrichmentService.populateCacheFromOrgMembers(fgaUserIds, orgMembers);
        this.loggerService.debug('FgaManager', `Primed ${populated} Auth0 users in enrichment cache`);
      }
    } catch (err) {
      // Non-critical: enrichment will happen lazily when users are loaded per group
      this.loggerService.warn('FgaManager', 'Failed to prime Auth0 user cache (will enrich lazily)', {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }

  /**
   * Eagerly fetch M2M client names from Auth0 Management API
   * to pre-populate the M2M client cache.
   *
   * When the cache is warm, group user lists immediately display
   * friendly names and ⚙️ icons for M2M clients.
   */
  private async primeM2MClients(): Promise<void> {
    if (!this.m2mClientService.configured()) {
      return;
    }

    try {
      this.loggerService.debug('FgaManager', 'Priming M2M client cache');
      const clients = await this.m2mClientService.loadClients();
      this.loggerService.debug('FgaManager', `Primed ${clients.length} M2M clients in cache`);
    } catch (err) {
      this.loggerService.warn('FgaManager', 'Failed to prime M2M client cache', {
        error: err instanceof Error ? err.message : String(err)
      });
    }
  }

  /**
   * Normalize a URL by trimming whitespace and trailing slashes.
   * Adds https:// prefix when requested and no protocol is present.
   */
  private normalizeUrl(value: string, forceHttps = false): string {
    const trimmed = value.trim().replace(/\/+$/, '');
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return forceHttps ? `https://${trimmed}` : trimmed;
  }

  // ---------------------------------------------------------------------------
  // Data Loading Methods
  // ---------------------------------------------------------------------------

  /**
   * Loads groups from the FGA API and populates the group state.
   * Called automatically after successful component initialization.
   * Handles loading, error states, and signal updates.
   */
  private loadGroups(): void {
    // Ensure organisationId is set (from input or token)
    if (!this.organisationId) {
      const errorEvent: FgaErrorEvent = {
        code: FGA_ERROR_CODES.MISSING_ORG_ID,
        message: 'Cannot load groups: organisation ID not available'
      };
      this.errorEvent.emit(errorEvent);
      this.groupStateService.setError('Organisation ID not available');
      return;
    }

    // Set loading state
    this.groupStateService.setLoading(true);

    this.fgaApi.getGroups(this.organisationId).subscribe({
      next: (response) => {
        // Convert raw group IDs to FgaGroup entities
        const groups: FgaGroup[] = response.groups.map(groupId => {
          // Extract group name from format: group:organisationId|groupName
          const parts = groupId.split(/[:|]/);

          const name = 
            parts.length > 2 ? parts[2] :
            parts.length > 1 ? parts[1] :
            groupId;

          return {
            id: groupId,
            name,
            organisationId: this.organisationId!,
            loading: false
          };
        });

        // Update state
        this.groupStateService.setGroups(groups);
        this.groupStateService.setLoading(false);

        if (!this.groupStateService.selectedGroupId() && groups.length > 0) {
          this.groupStateService.selectGroup(groups[0].id);
        }

        this.cdr.markForCheck();
        void this.enrichEnterpriseGroupNames(groups);
      },
      error: (error) => {
        // Check for 401/403 authorization errors
        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
          this.handleUnauthorizedError(error);
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Failed to load groups';
        
        // Emit error event
        const errorEvent: FgaErrorEvent = {
          code: FGA_ERROR_CODES.API_ERROR,
          message: `Failed to load groups: ${errorMessage}`
        };
        this.errorEvent.emit(errorEvent);

        // Update state
        this.groupStateService.setError(errorMessage);
        this.groupStateService.setLoading(false);
        this.cdr.markForCheck();
      }
    });
  }

  private async enrichEnterpriseGroupNames(groups: FgaGroup[]): Promise<void> {
    const resolvedNames = await this.auth0OrgService.resolveGroupNames(
      groups.map(group => group.name)
    );

    // Apply to current state: groups created or removed during the lookup must survive.
    this.groupStateService.setGroups(
      this.groupStateService.groups().map(group => ({ ...group, name: resolvedNames[group.name] ?? group.name }))
    );
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Protected Methods (for template)
  // ---------------------------------------------------------------------------

  /**
   * Handles group selection in the UI.
   *
   * @param groupId - ID of the selected group
   */
  protected onGroupSelect(groupId: string): void {
    this.loggerService.debug('FgaManager', 'Received groupId', { groupId });
    if (typeof groupId === 'string') {
      this.groupStateService.selectGroup(groupId);
    } else {
      this.loggerService.error('FgaManager', 'Invalid groupId emitted', { groupId });
    }
  }

  /**
   * Wrapper method to handle the selected event.
   * @param event The emitted event value.
   */
  handleGroupSelect(event: unknown): void {
    if (typeof event === 'string') {
      this.onGroupSelect(event);
    } else {
      this.loggerService.error('FgaManager', 'Invalid event type', { event });
    }
  }

  /**
   * Handles user selection in the UI.
   *
   * @param userId - ID of the selected user
   */
  protected onUserSelect(userId: string): void {
    this.userState.selectUser(userId);
    this.userSelectedCustom.emit({ userId });
  }

  protected onMembershipEnrichmentLoading(loading: boolean): void {
    this.loadingStateChanged.emit({ loading, source: 'memberships' });
  }

  onAddGroup(name: string): void {
    const orgId = this.organisationId;
    if (!orgId) {
      this.loggerService.error('FgaManager', 'Cannot add group: organisation ID not set');
      this.groupStateService.setAddGroupError('Organisation ID not configured');
      return;
    }

    // Set loading state and clear previous errors
    this.groupStateService.setAddingGroup(true);
    this.groupStateService.clearAddGroupError();

    this.fgaApi.createGroup(orgId, name).subscribe({
      next: result => {
        // Create FgaGroup object and add to state
        const newGroup: FgaGroup = {
          id: result.groupId,
          name: name,
          organisationId: orgId,
        };

        this.groupStateService.addGroup(newGroup);
        this.groupStateService.setAddingGroup(false);
        this.groupStateService.selectGroup(newGroup.id);

        // Emit data changed event
        this.dataChangedCustom.emit({
          type: 'group',
          action: 'create',
          entityId: result.groupId,
        });

        this.cdr.markForCheck();
      },
      error: (error: Error) => {
        this.loggerService.error('FgaManager', 'Failed to create group', { error: error.message });
        this.groupStateService.setAddGroupError(error.message || 'Failed to create group');
        this.groupStateService.setAddingGroup(false);
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Handles input changes in the add group field (clears API errors).
   */
  onAddGroupInputChange(): void {
    this.groupStateService.clearAddGroupError();
  }

  // ---------------------------------------------------------------------------
  // Unauthorized Screen Handlers
  // ---------------------------------------------------------------------------

  /**
   * Handles "Request Access" button click from unauthorized screen.
   * Emits event to host application for handling access request flow.
   */
  public onRequestAccess(): void {
    this.loggerService.info('FgaManager', 'Request access clicked');
    this.requestAccessEvent.emit();
  }

  /**
   * Handles "Retry" button click from unauthorized screen.
   * Clears error state and re-initializes the component.
   */
  public onRetry(): void {
    this.loggerService.info('FgaManager', 'Retry clicked, re-initializing');
    this.errorStateService.clearUnauthorized();
    this.retryEvent.emit();
    this.initialize();
  }

  // ---------------------------------------------------------------------------
  // Error Handling Helpers
  // ---------------------------------------------------------------------------

  /**
   * Handles HTTP 401/403 authorization errors.
   * Sets the error state and stops loading.
   *
   * @param error - The HTTP error response
   */
  private handleUnauthorizedError(error: HttpErrorResponse): void {
    const source = this.detectErrorSource(error.url ?? '');
    const message = this.extractErrorMessage(error);

    this.errorStateService.setUnauthorized({
      status: error.status as 401 | 403,
      source,
      message,
      timestamp: Date.now()
    });

    this.groupStateService.setLoading(false);
    this.cdr.markForCheck();
  }

  /**
   * Determines the error source based on the request URL.
   */
  private detectErrorSource(url: string): UnauthorizedErrorSource {
    const lowerUrl = url.toLowerCase();

    // FGA proxy patterns
    if (lowerUrl.includes('/stores/') || lowerUrl.includes('fga-api-proxy')) {
      return 'fga';
    }

    // Auth0 Management API patterns
    if (
      lowerUrl.includes('auth0.com') ||
      lowerUrl.includes('/api/v2/') ||
      lowerUrl.includes('auth0-management-api-proxy')
    ) {
      return 'auth0';
    }

    return 'unknown';
  }

  /**
   * Extracts error message from HTTP error response.
   */
  private extractErrorMessage(error: HttpErrorResponse): string {
    // Try to get message from response body
    if (error.error?.message) {
      return error.error.message;
    }

    if (error.error?.error) {
      return error.error.error;
    }

    // Default messages based on status
    if (error.status === 401) {
      return 'Authentication required';
    }

    if (error.status === 403) {
      return 'Access forbidden';
    }

    return error.message || 'An error occurred';
  }
}
